import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../lib/api";
import { useEmailSettings } from "./useEmailSettings";
import { Mail, Send, Loader2, CheckCircle2, XCircle, Users, AlertTriangle } from "lucide-react";

const CAMPAIGN = "architecture_update";
const LANG_LABEL = { en: "EN", it: "IT", es: "ES", de: "DE", fr: "FR", pl: "PL" };

export default function AdminBroadcastTab() {
  const { t } = useTranslation();
  const [previewLang, setPreviewLang] = useState("en");
  const [preview, setPreview] = useState(null);
  const [audience, setAudience] = useState("verified");
  const [rec, setRec] = useState(null);
  const { test_email: testEmail } = useEmailSettings();
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/admin/broadcast/preview", { params: { campaign: CAMPAIGN, lang: previewLang } })
      .then((r) => setPreview(r.data)).catch((e) => setError(e.message));
  }, [previewLang]);

  useEffect(() => {
    api.get("/admin/broadcast/recipients", { params: { campaign: CAMPAIGN, audience } })
      .then((r) => setRec(r.data)).catch((e) => setError(e.message));
  }, [audience, run?.status]);

  const pollRun = useCallback((runId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/broadcast/runs/${runId}`);
        setRun(data);
        if (data.status === "completed") { clearInterval(pollRef.current); setBusy(""); }
      } catch (e) { clearInterval(pollRef.current); setBusy(""); setError(e.message); }
    }, 1500);
  }, []);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const start = async (body, label) => {
    setError(""); setBusy(label); setRun(null);
    try {
      const { data } = await api.post("/admin/broadcast/send", { campaign: CAMPAIGN, ...body });
      setRun({ id: data.run_id, total: data.total, progress: 0, sent: 0, failed: 0, status: "running", results: [] });
      pollRun(data.run_id);
    } catch (e) {
      setBusy(""); setError(e.response?.data?.detail || e.message);
    }
  };

  const sendTest = () => start({ test: true, force_lang: previewLang }, "test");
  const sendAll = () => {
    const n = (rec?.total_recipients || 0) - (rec?.already_sent || 0);
    if (!n) return;
    if (!window.confirm(t("adminBroadcast.confirmSend", { n }))) return;
    start({ audience, confirm: true }, "all");
  };

  const pending = (rec?.total_recipients || 0) - (rec?.already_sent || 0);

  return (
    <div className="space-y-6" data-testid="admin-broadcast">
      <div>
        <h2 className="text-base md:text-lg font-display text-slate-100 flex items-center gap-2">
          <Mail size={16} className="text-amber-400"/>{t("adminBroadcast.title")}
        </h2>
        <p className="text-sm text-slate-400 max-w-3xl mt-1">{t("adminBroadcast.subtitle")}</p>
      </div>

      {error && <div className="text-sm text-red-400" data-testid="broadcast-error">{error}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* ---- Preview ---- */}
        <div className="tech-card p-4" data-testid="broadcast-preview-card">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="label-tech text-slate-400">{t("adminBroadcast.previewTitle")}</div>
            <div className="flex gap-1">
              {(rec?.languages_available || ["en", "it"]).map((l) => (
                <button key={l} onClick={() => setPreviewLang(l)}
                  className={`px-2 py-1 text-[11px] rounded border font-mono-data ${
                    previewLang === l ? "border-amber-500 text-amber-300 bg-amber-500/10"
                      : "border-slate-700 text-slate-400 hover:text-slate-200"}`}
                  data-testid={`broadcast-lang-${l}`}>
                  {LANG_LABEL[l] || l.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          <div className="text-xs text-slate-500 mb-1">{t("adminBroadcast.subjectLabel")}</div>
          <div className="text-sm text-slate-200 mb-3 font-mono-data" data-testid="broadcast-subject">
            {preview?.subject || "…"}
          </div>
          {preview?.html && (
            <iframe title="email-preview" srcDoc={preview.html} data-testid="broadcast-iframe"
              className="w-full h-[520px] rounded-md border border-slate-800 bg-[#0b1120]"/>
          )}
        </div>

        {/* ---- Audience + send ---- */}
        <div className="space-y-4">
          <div className="tech-card p-4" data-testid="broadcast-audience-card">
            <div className="label-tech text-slate-400 mb-3 flex items-center gap-2"><Users size={14}/>{t("adminBroadcast.audienceTitle")}</div>
            <div className="flex flex-wrap gap-2 mb-4">
              {["verified", "no_test", "all"].map((a) => (
                <button key={a} onClick={() => setAudience(a)}
                  className={`px-3 py-1.5 text-xs rounded border ${
                    audience === a ? "border-amber-500 text-amber-300 bg-amber-500/10"
                      : "border-slate-700 text-slate-400 hover:text-slate-200"}`}
                  data-testid={`broadcast-audience-${a}`}>
                  {t(`adminBroadcast.audience_${a}`)}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4 font-mono-data">
              <div><div className="text-[10px] label-tech text-slate-500">{t("adminBroadcast.accounts")}</div><div className="text-slate-200 text-lg">{rec?.total_accounts ?? "—"}</div></div>
              <div><div className="text-[10px] label-tech text-slate-500">{t("adminBroadcast.recipients")}</div><div className="text-amber-300 text-lg" data-testid="broadcast-recipients-count">{rec?.total_recipients ?? "—"}</div></div>
              <div><div className="text-[10px] label-tech text-slate-500">{t("adminBroadcast.alreadySent")}</div><div className="text-emerald-300 text-lg">{rec?.already_sent ?? "—"}</div></div>
            </div>
            <div className="flex flex-wrap gap-1 mb-3">
              {Object.entries(rec?.by_lang || {}).map(([l, n]) => (
                <span key={l} className="px-1.5 py-0.5 rounded border border-slate-700 text-[10px] font-mono-data text-slate-300">
                  {LANG_LABEL[l] || l} {n}
                </span>
              ))}
            </div>
            <div className="max-h-44 overflow-y-auto border-t border-slate-800 pt-2 space-y-1" data-testid="broadcast-recipient-list">
              {(rec?.recipients || []).map((r) => (
                <div key={r.email} className="flex items-center gap-2 text-[11px] font-mono-data">
                  <span className="text-slate-300 truncate">{r.email}</span>
                  <span className="text-slate-500">{LANG_LABEL[r.lang] || r.lang}</span>
                  {r.already_sent && <span className="ml-auto text-emerald-400">{t("adminBroadcast.sentBadge")}</span>}
                </div>
              ))}
              {rec && !rec.recipients.length && <div className="text-xs text-slate-500">{t("adminBroadcast.noRecipients")}</div>}
            </div>
          </div>

          <div className="tech-card p-4" data-testid="broadcast-send-card">
            <div className="label-tech text-slate-400 mb-3">{t("adminBroadcast.sendTitle")}</div>
            <div className="flex flex-col sm:flex-row gap-2 mb-3">
              <div className="flex-1 bg-slate-900 border border-slate-700 rounded px-3 py-2 text-sm text-slate-400 font-mono-data"
                data-testid="broadcast-test-email">{testEmail || "…"}</div>
              <button onClick={sendTest} disabled={!testEmail || !!busy}
                className="px-4 py-2 rounded border border-slate-600 text-sm text-slate-200 hover:border-amber-500 hover:text-amber-300 disabled:opacity-40 flex items-center justify-center gap-2"
                data-testid="broadcast-send-test">
                {busy === "test" ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
                {t("adminBroadcast.sendTest")}
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mb-4">{t("adminBroadcast.testHint")}</p>

            <button onClick={sendAll} disabled={!pending || !!busy}
              className="btn-primary w-full px-4 py-3 rounded-md text-sm flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="broadcast-send-all">
              {busy === "all" ? <Loader2 size={16} className="animate-spin"/> : <Send size={16}/>}
              {t("adminBroadcast.sendAll", { n: pending })}
            </button>
            <p className="text-[11px] text-amber-400/80 mt-3 flex items-start gap-1.5">
              <AlertTriangle size={12} className="mt-0.5 shrink-0"/>{t("adminBroadcast.warning")}
            </p>
          </div>

          {run && (
            <div className="tech-card p-4" data-testid="broadcast-run-card">
              <div className="flex items-center justify-between mb-2">
                <div className="label-tech text-slate-400">{t("adminBroadcast.runTitle")}</div>
                <div className="font-mono-data text-xs text-slate-400">
                  {run.progress || 0}/{run.total} · <span className="text-emerald-300">{run.sent || 0} ok</span>
                  {!!run.failed && <span className="text-rose-300"> · {run.failed} ko</span>}
                </div>
              </div>
              <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden mb-3">
                <div className="h-full bg-amber-500 transition-all"
                  style={{ width: `${run.total ? ((run.progress || 0) / run.total) * 100 : 0}%` }}/>
              </div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {(run.results || []).map((r, i) => (
                  <div key={r.email} className="flex items-center gap-2 text-[11px] font-mono-data" data-testid={`broadcast-result-${i}`}>
                    {r.sent ? <CheckCircle2 size={12} className="text-emerald-400 shrink-0"/> : <XCircle size={12} className="text-rose-400 shrink-0"/>}
                    <span className="text-slate-300 truncate">{r.email}</span>
                    <span className="text-slate-500">{LANG_LABEL[r.lang] || r.lang}</span>
                    {r.error && <span className="ml-auto text-rose-300 truncate max-w-[45%]">{r.error}</span>}
                    {!r.error && r.mode === "preview" && <span className="ml-auto text-amber-300">preview mode</span>}
                  </div>
                ))}
              </div>
              {run.status === "completed" && (
                <div className="text-xs text-emerald-300 mt-3" data-testid="broadcast-run-done">{t("adminBroadcast.runDone")}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
